import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Provider, inject } from '@angular/core';

import { PostHogService } from './posthog.service';

@Injectable({
  providedIn: 'root',
})
export class PostHogErrorHandler implements ErrorHandler {
  readonly #posthogService = inject(PostHogService);

  public handleError(error: unknown): void {
    const extractedError = this.#extractError(error);

    if (extractedError) {
      runOutsideAngular(() => this.#posthogService.posthog.captureException(extractedError));
    }

    console.error(error);
  }

  #extractError(errorCandidate: unknown): Error | string | null {
    const error = unwrapZoneError(errorCandidate);

    if (error instanceof HttpErrorResponse) {
      return error.error instanceof Error ? error.error : error.message;
    }

    if (typeof error === 'string' || isErrorLike(error)) {
      return error;
    }

    return null;
  }
}

function unwrapZoneError(error: unknown): unknown {
  if (error && typeof error === 'object' && 'ngOriginalError' in error) {
    return (error as { ngOriginalError: unknown }).ngOriginalError;
  }

  return error;
}

function isErrorLike(value: unknown): value is Error {
  if (value instanceof Error) {
    return true;
  }

  return value !== null && typeof value === 'object' && !Array.isArray(value) && 'name' in value && 'message' in value && 'stack' in value;
}

declare const Zone: { root?: { run<T>(callback: () => T): T } } | undefined;

export function runOutsideAngular<T>(callback: () => T): T {
  return Zone?.root ? Zone.root.run(callback) : callback();
}

export function providePostHogErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useExisting: PostHogErrorHandler,
  };
}
